/**
 * Adaptive Learning Configuration
 * Constants, enums, and tier configurations
 */

// Progress Tier Enum
export const ProgressTier = {
  BEGINNER: "BEGINNER",
  BRONZE: "BRONZE",
  SILVER: "SILVER",
  GOLD: "GOLD",
  MASTERED: "MASTERED",
};

// Session Status Enum
export const SessionStatus = {
  STARTED: "STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  ABANDONED: "ABANDONED",
};

// Question Type Enum
export const QuestionType = {
  MULTIPLE_CHOICE: "MULTIPLE_CHOICE",
  TRUE_FALSE: "TRUE_FALSE",
  SHORT_ANSWER: "SHORT_ANSWER",
};

// Difficulty Enum
export const Difficulty = {
  BEGINNER: "BEGINNER",
  EASY: "EASY",
  MEDIUM: "MEDIUM",
  HARD: "HARD",
  EXPERT: "EXPERT",
};

// Tier Configuration for UI Display
export const TIER_CONFIG = {
  BEGINNER: {
    name: "Beginner",
    icon: "🌱",
    color: "#9CA3AF",
    backgroundColor: "#F3F4F6",
    borderColor: "#D1D5DB",
    gradientFrom: "#F9FAFB",
    gradientTo: "#F3F4F6",
    range: "0-30%",
    description: "Just starting out",
    minPercentage: 0,
    maxPercentage: 30,
  },
  BRONZE: {
    name: "Bronze",
    icon: "🥉",
    color: "#CD7F32",
    backgroundColor: "#FEF3C7",
    borderColor: "#F59E0B",
    gradientFrom: "#FEF3C7",
    gradientTo: "#FDE68A",
    range: "30-60%",
    description: "Basic understanding",
    minPercentage: 30,
    maxPercentage: 60,
  },
  SILVER: {
    name: "Silver",
    icon: "🥈",
    color: "#C0C0C0",
    backgroundColor: "#E5E7EB",
    borderColor: "#9CA3AF",
    gradientFrom: "#F3F4F6",
    gradientTo: "#E5E7EB",
    range: "60-85%",
    description: "Strong grasp",
    minPercentage: 60,
    maxPercentage: 85,
  },
  GOLD: {
    name: "Gold",
    icon: "🥇",
    color: "#FFD700",
    backgroundColor: "#FEF3C7",
    borderColor: "#F59E0B",
    gradientFrom: "#FEF9C3",
    gradientTo: "#FDE047",
    range: "85-95%",
    description: "Excellent mastery",
    minPercentage: 85,
    maxPercentage: 95,
  },
  MASTERED: {
    name: "Mastered",
    icon: "👑",
    color: "#9333EA",
    backgroundColor: "#F3E8FF",
    borderColor: "#A855F7",
    gradientFrom: "#FAF5FF",
    gradientTo: "#F3E8FF",
    range: "95-100%",
    description: "Complete mastery",
    minPercentage: 95,
    maxPercentage: 100,
  },
};

// Get tier configuration by tier name
export const getTierConfig = (tier) => {
  return TIER_CONFIG[tier] || TIER_CONFIG.BEGINNER;
};

// Get tier by mastery percentage
export const getTierByPercentage = (percentage) => {
  if (percentage >= 95) return ProgressTier.MASTERED;
  if (percentage >= 85) return ProgressTier.GOLD;
  if (percentage >= 60) return ProgressTier.SILVER;
  if (percentage >= 30) return ProgressTier.BRONZE;
  return ProgressTier.BEGINNER;
};

// Check if tier has advanced
export const hasTierAdvanced = (previousTier, newTier) => {
  const tierOrder = [
    ProgressTier.BEGINNER,
    ProgressTier.BRONZE,
    ProgressTier.SILVER,
    ProgressTier.GOLD,
    ProgressTier.MASTERED,
  ];

  const previousIndex = tierOrder.indexOf(previousTier);
  const newIndex = tierOrder.indexOf(newTier);

  return newIndex > previousIndex;
};

// Default number of questions per session
export const DEFAULT_QUESTIONS_PER_SESSION = 10;

// Polling interval for question generation (milliseconds)
export const QUESTION_POLLING_INTERVAL = 2000;

// Max retries for fetching questions
export const MAX_QUESTION_FETCH_RETRIES = 5;

// Loading messages
export const LOADING_MESSAGES = {
  generatingQuestions: "AI is generating personalized questions for you...",
  evaluatingAnswers: "AI is evaluating your answers...",
  loadingProgress: "Loading your progress...",
  startingSession: "Starting your learning session...",
};

// Error messages
export const ERROR_MESSAGES = {
  conceptNotFound: "Concept not found. Please try again.",
  serverError: "Unable to generate questions. Please try again.",
  networkError: "Something went wrong. Please check your connection.",
  submissionFailed: "Failed to submit answers. Please try again.",
  questionsNotReady: "Questions are still being generated. Please wait a moment and click 'Retry'.",
  sessionStartFailed: "Failed to start learning session. Please try again.",
  questionsFetchFailed: "Questions are still being generated. Click 'Retry' to load them.",
};

// Subject options (extend as needed)
export const SUBJECTS = {
  PHYSICS: "PHYSICS",
  CHEMISTRY: "CHEMISTRY",
  MATHEMATICS: "MATHEMATICS",
  BIOLOGY: "BIOLOGY",
};

// Grade options
export const GRADES = [
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
];

export default {
  ProgressTier,
  SessionStatus,
  QuestionType,
  Difficulty,
  TIER_CONFIG,
  getTierConfig,
  getTierByPercentage,
  hasTierAdvanced,
  DEFAULT_QUESTIONS_PER_SESSION,
  QUESTION_POLLING_INTERVAL,
  MAX_QUESTION_FETCH_RETRIES,
  LOADING_MESSAGES,
  ERROR_MESSAGES,
  SUBJECTS,
  GRADES,
};

